import React, { useState } from "react";
import * as FiIcons from "react-icons/fi";
import {
  SettingsActions,
  SettingsCard,
  SettingsField,
  SettingsNotice,
} from "./SettingsPanel";

const { FiShield } = FiIcons;

export default function SecuritySettings({
  auth,
  userId,
  onPasswordChanged,
}) {
  const [editing, setEditing] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const cancel = () => {
    setEditing(false);
    setPassword("");
    setConfirmation("");
    setMessage(null);
  };

  const save = async (event) => {
    event.preventDefault();
    setMessage(null);

    if (
      password.length < 8 ||
      !/[a-z]/.test(password) ||
      !/[A-Z]/.test(password) ||
      !/[0-9]/.test(password)
    ) {
      setMessage({
        kind: "error",
        text: "Use at least 8 characters with an uppercase letter, a lowercase letter, and a number.",
      });
      return;
    }

    if (password !== confirmation) {
      setMessage({
        kind: "error",
        text: "The new password and confirmation do not match.",
      });
      return;
    }

    setSaving(true);

    try {
      await onPasswordChanged(password);
      setPassword("");
      setConfirmation("");
      setEditing(false);
      setMessage({
        kind: "success",
        text: "Supabase confirmed the password update.",
      });
    } catch (error) {
      setMessage({
        kind: "error",
        text: error.message || "Unable to change your password.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsCard
      title="Security"
      description="Authentication details and account password."
      icon={FiShield}
    >
      {message && (
        <div className="mb-4">
          <SettingsNotice kind={message.kind}>
            {message.text}
          </SettingsNotice>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <p className="text-xs font-semibold text-slate-600">
            Account Email
          </p>
          <p className="mt-1.5 min-h-10 break-all rounded-xl bg-slate-50 px-3 py-2.5 text-sm text-slate-700">
            {auth?.email || "Unavailable"}
          </p>
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-600">
            Sign-in Provider
          </p>
          <p className="mt-1.5 min-h-10 rounded-xl bg-slate-50 px-3 py-2.5 text-sm capitalize text-slate-700">
            {auth?.provider || "Unavailable"}
          </p>
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-600">
            Email Verification
          </p>
          <p className="mt-1.5 min-h-10 rounded-xl bg-slate-50 px-3 py-2.5 text-sm text-slate-700">
            {auth?.email
              ? auth.emailVerified
                ? "Verified"
                : "Not verified"
              : "Unavailable"}
          </p>
        </div>
      </div>

      <div className="mt-5 border-t border-slate-100 pt-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              Change Password
            </h3>
            <p className="mt-1 text-xs leading-5 text-slate-500">
              Password changes are processed by Supabase Auth.
            </p>
          </div>
          {auth?.supportsPassword && !editing && (
            <button
              type="button"
              onClick={() => {
                setMessage(null);
                setEditing(true);
              }}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Change password
            </button>
          )}
        </div>

        {!auth?.supportsPassword && (
          <div className="mt-4">
            <SettingsNotice>
              Password changes are not available for this sign-in provider. Manage credentials with your identity provider.
            </SettingsNotice>
          </div>
        )}

        {auth?.supportsPassword && editing && (
          <form onSubmit={save} className="mt-4 space-y-4">
            <SettingsNotice>
              Use at least 8 characters, including uppercase, lowercase, and a number. Supabase may require a recent sign-in.
            </SettingsNotice>
            <div className="grid gap-4 sm:grid-cols-2">
              <SettingsField label="New Password">
                <input
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                />
              </SettingsField>
              <SettingsField label="Confirm New Password">
                <input
                  type="password"
                  autoComplete="new-password"
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                />
              </SettingsField>
            </div>
            <SettingsActions
              onCancel={cancel}
              saving={saving}
              disabled={!password || !confirmation}
            />
          </form>
        )}
      </div>
    </SettingsCard>
  );
}